import type {
  NarrativeHeadlineOption,
  SensemakingCatalyst,
} from "../components/narrative/sensemaking-types";

export interface NarrativeBootstrapDraftState {
  openerQuery: string | null;
  catalystIds: string[];
}

export function headlineToDraftCatalyst(
  headline: NarrativeHeadlineOption,
): SensemakingCatalyst {
  return {
    id: headline.id,
    headline: headline.headline,
    summary: headline.summary,
    source: headline.source,
    category: headline.tags[0] ?? "macroeconomic",
    sentiment: headline.severity,
    ivScore: headline.ivScore ?? 0,
    publishedAt: headline.publishedAt,
    promotedAt: null,
    symbols: headline.symbols,
    tags: headline.tags,
    narrativeThreads: headline.narrativeThreads,
    marketImpact: null,
    agentNote: null,
    role: "anchor",
    relationScore: 1,
    relationReason: "Bootstrap draft anchor from attached RiskFlow catalyst.",
  };
}

export function resolveBootstrapDraftCatalysts(
  catalystIds: string[],
  headlines: NarrativeHeadlineOption[],
): SensemakingCatalyst[] {
  if (catalystIds.length === 0) return [];
  const byId = new Map(headlines.map((headline) => [headline.id, headline]));
  return catalystIds
    .map((id) => byId.get(id))
    .filter((headline): headline is NarrativeHeadlineOption =>
      Boolean(headline),
    )
    .map(headlineToDraftCatalyst);
}

export function readBootstrapDraftState(
  artifacts?: Record<string, { payload?: Record<string, unknown> }>,
): NarrativeBootstrapDraftState {
  const payload = artifacts?.["agent-work"]?.payload;
  if (!payload || payload.kind !== "bootstrap-draft") {
    return { openerQuery: null, catalystIds: [] };
  }
  return {
    openerQuery:
      typeof payload.openerQuery === "string" && payload.openerQuery.trim()
        ? payload.openerQuery.trim()
        : null,
    catalystIds: Array.isArray(payload.catalystIds)
      ? payload.catalystIds.map(String).filter(Boolean)
      : [],
  };
}
